// Pathways v3, practitioner side: the data-testid registry of every admin pathway surface
// (contract §9: the list, the builder and its step editor, the preview, the dashboard, and
// the CRM contact's Pathways tab). The learner lane's ids are in ./pathwaysLearner.js.
//
// The components render these ids and the jest suites read them. An id that depends on a
// row is a function of that row's key, so the two sides cannot disagree on the separator.

export const PATHWAYS_ADMIN = Object.freeze({
  // ── list (admin/PathwaysAdmin.jsx) ──
  list: "pathways-admin",
  listGrid: "pathways-admin-grid",
  listEmpty: "pathways-admin-empty",
  listLoadError: "pathways-admin-load-error",
  create: "pathways-admin-create",
  planNotice: "pathways-plan-notice",
  card: (id) => `pathway-card-${id}`,
  cardOpen: (id) => `pathway-card-open-${id}`,               // <a> to the dashboard
  cardType: (id) => `pathway-card-type-${id}`,               // data-type = successive | alternative | level
  cardStatus: (id) => `pathway-card-status-${id}`,           // data-status = draft | published | archived
  cardEnrolled: (id) => `pathway-card-enrolled-${id}`,
  cardProgress: (id) => `pathway-card-progress-${id}`,
  cardMenu: (id) => `pathway-card-menu-${id}`,               // the ⋮ button
  cardMenuEdit: (id) => `pathway-card-menu-edit-${id}`,
  cardMenuDuplicate: (id) => `pathway-card-menu-duplicate-${id}`,
  cardMenuArchive: (id) => `pathway-card-menu-archive-${id}`,

  // ── builder (admin/pathways/PathwayBuilder.jsx) ──
  builder: "pathway-builder",
  builderBack: "pathway-builder-back",
  builderTitle: "pathway-builder-title",
  builderStatus: "pathway-builder-status",                   // data-status
  builderUnsaved: "pathway-builder-unsaved",
  builderReadOnly: "pathway-builder-read-only",
  tabBasics: "pathway-builder-tab-basics",
  tabStructure: "pathway-builder-tab-structure",
  previewButton: "pathway-builder-preview",
  saveDraft: "pathway-builder-save",
  activate: "pathway-builder-activate",
  saveError: "pathway-builder-save-error",
  problems: "pathway-builder-problems",                      // the activation banner
  problem: (i) => `pathway-builder-problem-${i}`,            // <button>: jumps to its level / step

  // ── Basics (admin/pathways/BasicsPanel.jsx) ──
  basics: "pathway-basics",
  name: "pathway-basics-name",
  description: "pathway-basics-description",
  objective: (i) => `pathway-basics-objective-${i}`,
  objectiveRemove: (i) => `pathway-basics-objective-remove-${i}`,
  objectiveAdd: "pathway-basics-objective-add",
  certificate: "pathway-basics-certificate",                 // role=switch, aria-checked
  pathType: (type) => `pathway-basics-type-${type}`,         // the radio input
  pathTypeLockedNote: "pathway-basics-type-locked",
  schedule: (value) => `pathway-basics-schedule-${value}`,   // cohort | self_paced (radio input)
  startDate: "pathway-basics-start-date",
  endDate: "pathway-basics-end-date",
  capacity: "pathway-basics-capacity",
  price: "pathway-basics-price",
  priceCurrency: "pathway-basics-price-currency",
  mergeConfirm: "pathway-basics-merge-confirm",
  mergeConfirmYes: "pathway-basics-merge-confirm-yes",
  mergeConfirmNo: "pathway-basics-merge-confirm-no",

  // ── Structure (admin/pathways/StructureCanvas.jsx) ──
  canvas: "pathway-structure",
  level: (key) => `pathway-level-${key}`,
  levelTitle: (key) => `pathway-level-title-${key}`,
  levelDrag: (key) => `pathway-level-drag-${key}`,
  levelUnlocks: (key) => `pathway-level-unlocks-${key}`,
  levelUp: (key) => `pathway-level-up-${key}`,
  levelDown: (key) => `pathway-level-down-${key}`,
  levelRemove: (key) => `pathway-level-remove-${key}`,
  addLevel: "pathway-structure-add-level",
  addStep: (levelKey) => `pathway-level-add-step-${levelKey}`,
  step: (key) => `pathway-step-${key}`,                      // <li>; data-kind
  stepDrag: (key) => `pathway-step-drag-${key}`,
  stepOpen: (key) => `pathway-step-open-${key}`,             // the title button: opens the editor
  stepPill: (key) => `pathway-step-pill-${key}`,             // Mandatory / Optional toggle; aria-pressed = mandatory
  stepDuration: (key) => `pathway-step-duration-${key}`,
  stepUnlocks: (key) => `pathway-step-unlocks-${key}`,
  stepUp: (key) => `pathway-step-up-${key}`,
  stepDown: (key) => `pathway-step-down-${key}`,
  stepRemove: (key) => `pathway-step-remove-${key}`,
  stepInvalid: (key) => `pathway-step-invalid-${key}`,
  stepLive: (key) => `pathway-step-live-${key}`,            // a live row's date/time or session type

  // ── step editor (admin/pathways/StepEditorModal.jsx) ──
  stepEditor: "pathway-step-editor",
  stepClose: "pathway-step-editor-close",                    // the sticky header's X
  stepTitle: "pathway-step-editor-title",
  stepDurationInput: "pathway-step-editor-duration",
  stepKind: (kind) => `pathway-step-editor-kind-${kind}`,    // role=radio, aria-checked
  stepKindLocked: "pathway-step-editor-kind-locked",         // a saved step: its kind, not a selector; data-kind
  videoUpload: "pathway-step-editor-video-upload",
  videoLink: "pathway-step-editor-video-link",
  videoFile: "pathway-step-editor-video-file",
  videoRemove: "pathway-step-editor-video-remove",
  videoPreview: "pathway-step-editor-video-preview",
  readingBody: "pathway-step-editor-reading-body",
  readingTool: (tool) => `pathway-step-editor-reading-tool-${tool}`,   // bold | italic | heading | bullets | link
  readingPreviewToggle: "pathway-step-editor-reading-preview-toggle",
  readingWriteToggle: "pathway-step-editor-reading-write-toggle",
  readingPreview: "pathway-step-editor-reading-preview",
  readingPreviewPane: "pathway-step-editor-reading-preview-pane",   // the bounded scroller around it
  readingPdfAttach: "pathway-step-editor-reading-pdf-attach",
  readingPdf: "pathway-step-editor-reading-pdf",
  readingPdfRemove: "pathway-step-editor-reading-pdf-remove",
  quizQuestion: (i) => `pathway-quiz-question-${i}`,
  quizChoice: (i, j) => `pathway-quiz-choice-${i}-${j}`,
  quizCorrect: (i, j) => `pathway-quiz-correct-${i}-${j}`,
  quizAddChoice: (i) => `pathway-quiz-add-choice-${i}`,
  quizRemoveChoice: (i, j) => `pathway-quiz-remove-choice-${i}-${j}`,
  quizRemoveQuestion: (i) => `pathway-quiz-remove-question-${i}`,
  quizAddQuestion: "pathway-quiz-add-question",
  quizThreshold: "pathway-quiz-threshold",
  quizImport: "pathway-quiz-import",
  quizImportSelect: "pathway-quiz-import-select",
  quizImportRun: "pathway-quiz-import-run",
  quizImportResult: "pathway-quiz-import-result",
  quizImportSkipped: "pathway-quiz-import-skipped",
  quizImportNone: "pathway-quiz-import-none",
  quizImportError: "pathway-quiz-import-error",
  practiceInstructions: "pathway-step-editor-practice-instructions",
  practiceSubmission: "pathway-step-editor-practice-submission",   // checkbox: Require submission
  livePanel: "pathway-step-editor-live",                     // data-mode = cohort | self_paced
  liveStartsAt: "pathway-step-editor-live-starts-at",        // datetime-local, browser zone
  liveService: "pathway-step-editor-live-service",           // <select> of active session types
  liveServiceNone: "pathway-step-editor-live-service-none",
  liveServiceError: "pathway-step-editor-live-service-error",
  liveLocation: "pathway-step-editor-live-location",
  coursePicker: "pathway-step-editor-course",                // <select> of published courses
  coursePickerNone: "pathway-step-editor-course-none",
  coursePickerError: "pathway-step-editor-course-error",
  courseFixed: "pathway-step-editor-course-fixed",           // a saved course step's course
  coursePaidWarning: "pathway-step-editor-course-paid",
  resourcesAttach: "pathway-step-editor-resources-attach",
  resource: (mediaId) => `pathway-step-editor-resource-${mediaId}`,
  resourceRemove: (mediaId) => `pathway-step-editor-resource-remove-${mediaId}`,
  stepSave: "pathway-step-editor-save",
  stepCancel: "pathway-step-editor-cancel",
  stepError: (field) => `pathway-step-editor-error-${field}`,

  // ── preview (admin/pathways/PathwayPreview.jsx) ──
  preview: "pathway-preview",
  previewBanner: "pathway-preview-banner",
  previewBack: "pathway-preview-back",

  // ── dashboard (admin/pathways/PathwayDashboard.jsx) ──
  dashboard: "pathway-dashboard",
  dashboardTitle: "pathway-dashboard-title",
  dashboardLoadError: "pathway-dashboard-load-error",
  exportCsv: "pathway-dashboard-export",
  dashboardMenu: "pathway-dashboard-menu",
  edit: "pathway-dashboard-edit",
  duplicate: "pathway-dashboard-duplicate",
  archive: "pathway-dashboard-archive",
  total: (name) => `pathway-dashboard-total-${name}`,       // enrolled | active_this_week | completed | average_progress | stuck
  funnel: "pathway-dashboard-funnel",
  funnelBar: (key) => `pathway-dashboard-funnel-bar-${key}`, // data-count; data-drop = "true" on the largest drop
  filterAll: "pathway-dashboard-filter-all",
  filterStuck: "pathway-dashboard-filter-stuck",
  encourage: "pathway-dashboard-encourage",
  learners: "pathway-dashboard-learners",
  learnerRow: (email) => `pathway-dashboard-learner-${email}`,
  learnerOpen: (email) => `pathway-dashboard-learner-open-${email}`,
  learnersEmpty: "pathway-dashboard-learners-empty",
  encourageModal: "pathway-encourage-modal",
  encourageRecipients: "pathway-encourage-recipients",
  encourageMessage: "pathway-encourage-message",
  encourageSend: "pathway-encourage-send",
  encourageError: "pathway-encourage-error",
  prompts: "pathway-dashboard-prompts",
  promptText: "pathway-dashboard-prompt-text",
  promptPost: "pathway-dashboard-prompt-post",
  prompt: (id) => `pathway-dashboard-prompt-${id}`,
  reviewQueue: "pathway-dashboard-review",
  reviewTitle: "pathway-dashboard-review-title",            // carries the pending count
  reviewEmpty: "pathway-dashboard-review-empty",
  reviewLoadError: "pathway-dashboard-review-load-error",
  reviewItem: (id) => `pathway-dashboard-review-${id}`,
  reviewText: (id) => `pathway-dashboard-review-text-${id}`,
  reviewPhoto: (id) => `pathway-dashboard-review-photo-${id}`,
  reviewApprove: (id) => `pathway-dashboard-review-approve-${id}`,
  reviewFeedback: (id) => `pathway-dashboard-review-feedback-${id}`,
  feedbackModal: "pathway-feedback-modal",
  feedbackMessage: "pathway-feedback-message",
  feedbackSend: "pathway-feedback-send",
  feedbackError: "pathway-feedback-error",
  attendance: (stepId) => `pathway-dashboard-attendance-${stepId}`,
  attendanceBox: (stepId, email) => `pathway-dashboard-attendance-${stepId}-${email}`,   // checkbox
  attendanceSave: (stepId) => `pathway-dashboard-attendance-save-${stepId}`,
  attendanceError: (stepId) => `pathway-dashboard-attendance-error-${stepId}`,

  // ── CRM client, Pathways tab (admin/crm/ContactPathways.jsx; the tab bar is the CRM's) ──
  crmPathways: "crm-pathways",
  crmPathwaysEmpty: "crm-pathways-empty",
  crmPathwaysLoadError: "crm-pathways-load-error",
  crmPathway: (id) => `crm-pathway-${id}`,                   // data-open = "true" when expanded
  crmPathwayToggle: (id) => `crm-pathway-toggle-${id}`,
  crmPathwayStep: (pathwayId, stepId) => `crm-pathway-step-${pathwayId}-${stepId}`, // data-state
  crmPathwaySubmission: (pathwayId, stepId) => `crm-pathway-submission-${pathwayId}-${stepId}`, // data-status
  crmPathwayAttended: (pathwayId, stepId) => `crm-pathway-attended-${pathwayId}-${stepId}`,
});
